import { ChangeEvent, useEffect, useRef, useState } from "react";
import JicFloatingWidget from "./jicFloatingWidget";
import LoveTransferPanel from "./loveTransferPanel";
import {
  describeFace,
  loadFaceRecognitionModels,
  readLocalFaceRegistry,
  resolveLocalFace,
} from "../lib/faceRecognition";
import { fetchFaceStats, fetchFaceStatus, resolveFaceOnServer } from "../lib/faceRegistry";
import { fetchJicHandle, getJicToken, getJicUser, onJicChange, type JicUser } from "../lib/jic";

// face.jic.io 首页 — 小白极简:一个大按钮「扫一扫」,扫到脸 → 送 LOVE。
type Screen = "home" | "scanning" | "matched";

type ResolvedFace = {
  handle: string;
  faceId: string;
  address?: string;
  verified: boolean;
  mode?: "personal" | "relay" | "prereg";
  label?: string;
};

export default function FaceScanner() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const analyzingRef = useRef(false);
  const [screen, setScreen] = useState<Screen>("home");
  const [message, setMessage] = useState("");
  const [match, setMatch] = useState<ResolvedFace | null>(null);
  const [registryCount, setRegistryCount] = useState<number | null>(null);
  const [jicUser, setJicUser] = useState<JicUser | null>(() => getJicUser());
  const [myName, setMyName] = useState<string | null>(null);
  const [myBound, setMyBound] = useState<boolean | null>(null);

  const stopScanner = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    loadFaceRecognitionModels().catch(() => undefined);
    fetchFaceStats().then((count) => setRegistryCount(count ?? readLocalFaceRegistry().length));
    return stopScanner;
  }, []);

  useEffect(() => onJicChange(setJicUser), []);

  // 登录后:查自己名字与绑定状态,首页给出对应的「我的脸」入口
  useEffect(() => {
    let cancelled = false;
    setMyName(null);
    setMyBound(null);
    if (!jicUser?.sub || !getJicToken()) return;
    (async () => {
      const name = await fetchJicHandle(jicUser.sub);
      if (cancelled) return;
      setMyName(name);
      if (!name) return;
      const status = await fetchFaceStatus(name);
      if (!cancelled) setMyBound(Boolean(status?.bound));
    })();
    return () => {
      cancelled = true;
    };
  }, [jicUser?.sub]);

  const resolveInput = async (input: HTMLVideoElement | HTMLImageElement) => {
    if (analyzingRef.current) return false;
    analyzingRef.current = true;
    try {
      const descriptor = await describeFace(input);
      if (!descriptor) {
        setMessage("对准正脸,再近一点");
        return false;
      }
      const server = await resolveFaceOnServer(descriptor);
      if (server?.match) {
        const m = server.match;
        const isAnchor = m.mode === "relay" || m.mode === "prereg";
        setMatch({
          handle: m.name,
          faceId: isAnchor ? m.label || m.name : `@${m.name}`,
          address: m.address || undefined,
          verified: isAnchor ? Boolean(m.address) : true,
          mode: m.mode || "personal",
          label: m.label,
        });
        setScreen("matched");
        stopScanner();
        return true;
      }
      const local = resolveLocalFace(descriptor);
      if (local) {
        setMatch({
          handle: local.record.handle,
          faceId: local.record.faceId,
          address: local.record.controllerAddress,
          verified: Boolean(local.record.addressVerified),
          mode: "personal",
        });
        setScreen("matched");
        stopScanner();
        return true;
      }
      setMessage("这张脸还没绑定,换一个人试试");
      return false;
    } catch {
      setMessage("认不出来,再试一次");
      return false;
    } finally {
      analyzingRef.current = false;
    }
  };

  const startCamera = async () => {
    stopScanner();
    setMatch(null);
    setScreen("scanning");
    setMessage("正在打开相机…");
    try {
      await loadFaceRecognitionModels();
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("这个浏览器用不了相机");
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 960 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (!videoRef.current) throw new Error("相机打不开");
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setMessage("对准一张脸…");
      timerRef.current = window.setInterval(() => {
        if (videoRef.current?.readyState && videoRef.current.readyState >= 2) {
          void resolveInput(videoRef.current);
        }
      }, 720);
    } catch (error) {
      setScreen("home");
      setMessage(error instanceof Error ? error.message : "相机打不开");
      stopScanner();
    }
  };

  const cancelScan = () => {
    stopScanner();
    setScreen("home");
    setMessage("");
  };

  const onPhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    stopScanner();
    setMatch(null);
    setScreen("scanning");
    setMessage("正在认照片里的脸…");
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = async () => {
      try {
        const hit = await resolveInput(image);
        if (!hit) setScreen("home");
      } finally {
        URL.revokeObjectURL(objectUrl);
        event.target.value = "";
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setScreen("home");
      setMessage("照片打不开,换一张");
    };
    image.src = objectUrl;
  };

  const myFace = myName && myBound
    ? { text: `我的脸已绑定 · @${myName}`, href: `/${myName}`, icon: "✓" }
    : { text: "绑定我的脸,别人扫我就能送我", href: myName ? `/${myName}` : "/me", icon: "🙂" };

  return (
    <main className="fs-page">
      <div className="face3-aurora face3-aurora-one" />
      <div className="face3-aurora face3-aurora-two" />

      {screen === "home" && (
        <section className="fs-home">
          <div className="fs-brand"><span>F³</span> FACE</div>
          <h1 className="fs-title">扫脸,送 LOVE</h1>
          <button type="button" className="fs-scan-btn" onClick={startCamera} aria-label="扫一扫">
            <i>📷</i>
            <b>扫一扫</b>
          </button>
          <div className="fs-links">
            <button type="button" className="fs-link" onClick={() => fileRef.current?.click()}>
              🖼 从相册选照片
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} hidden />
            <a className="fs-link" href={myFace.href}>{myFace.icon} {myFace.text}</a>
          </div>
          {message && <p className="fs-msg">{message}</p>}
          <p className="fs-foot">{registryCount === null ? " " : `已有 ${registryCount} 张脸可以扫`}</p>
        </section>
      )}

      {screen !== "home" && (
        <section className="fs-scan">
          <div className={`fs-camera ${screen === "scanning" ? "is-live" : ""}`}>
            <video ref={videoRef} playsInline muted />
            <div className="fs-frame" />
            {screen === "scanning" && <div className="fs-scanline" />}
            {screen === "matched" && match && (
              <div className="fs-hit">
                <span>{match.mode === "relay" ? "⛪" : match.mode === "prereg" ? "◇" : "🙂"}</span>
                <strong>{match.faceId}</strong>
                {match.verified && <em>✓</em>}
              </div>
            )}
          </div>
          {screen === "scanning" && (
            <>
              <p className="fs-msg">{message}</p>
              <button type="button" className="fs-link" onClick={cancelScan}>✕ 取消</button>
            </>
          )}
          {screen === "matched" && match && (
            <>
              <LoveTransferPanel
                faceId={match.faceId}
                handle={match.handle}
                recipient={match.address}
                verified={match.verified}
              />
              <button type="button" className="fs-link" onClick={cancelScan}>← 再扫一个</button>
            </>
          )}
        </section>
      )}
      <JicFloatingWidget />
    </main>
  );
}
